import {Component, computed, effect, signal, Signal} from '@angular/core';
import {Apollo, QueryRef} from 'apollo-angular';
import {DesktopHost, EditingWorkspace, Organization, Query, RepositoryProcessPayload} from '../../types-repository';
import {
  GET_HOSTS_AND_WORKSPACES,
  MOUNT_EDITING_WORKSPACE,
  SETUP_DESKTOP_HOST,
  START_DESKTOP_HOST,
  STOP_DESKTOP_HOST,
  UNMOUNT_EDITING_WORKSPACE
} from '../../queries-repository';
import {ContextService} from '../../services/context.service';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {of} from 'rxjs';
import {catchError, filter, map, switchMap, tap} from 'rxjs/operators';
import {BreadCrumb, LogoAreaComponent} from '../logo-area/logo-area.component';
import {HeadlineSectionComponent} from '../headline-section/headline-section.component';
import {CommonModule} from '@angular/common';
import {RouterLink} from '@angular/router';
import {DateComponent} from '../../date/date.component';
import {IsAllowedAnywherePipe} from '../../pipes/has-action-pipe';
import {BannerService} from '../../banner/banner.service';
import {Dialog} from '@angular/cdk/dialog';
import {ConfirmDialogComponent} from '../../confirm-dialog/confirm-dialog.component';
import {MountDialogComponent} from './mount-dialog.component';
import {mountPool, POOL_RESOURCE} from './mount-pool';
import {crdListState, CrdListState} from './crd-list-state';
import {firstGrantedPattern, grantedPatterns} from '../../services/policy-access';
import {ProcessButtonService} from '../../services/process-button.service';
import {ProcessButtonDirective} from '../../process-button.directive';
import {PendingWorkspace, PendingWorkspacesService} from '../../services/pending-workspaces.service';

interface HostView {
  host: DesktopHost;
  workspaces: EditingWorkspace[];
}

/** A workspace this browser asked for that has no volume (yet): the row shows its process. */
export interface PendingRow {
  entry: PendingWorkspace;
  process?: RepositoryProcessPayload;
  /** Browse links to the source folders (/o/<org URN>/files/browse/<bucket>/<path>/). */
  sourceFolders: { link: string, name: string }[];
}

interface RegionView {
  region: string;
  city: string;
  pending: PendingRow[];
  unassigned: EditingWorkspace[];
  hosts: HostView[];
}

/** Browser and AWS clocks differ; a volume created this long before its pending entry still belongs to it. */
const CLOCK_SKEW_MS = 5 * 60 * 1000;

/** Same pool, name and AZ, and created after the entry started (an older workspace of the same name is not it). */
function couldBeCreating(entry: PendingWorkspace, w: EditingWorkspace): boolean {
  return w.pool === entry.pool && w.mountPoint === entry.mountPoint && w.availabilityZone === entry.availabilityZone
    && w.createdAt * 1000 >= entry.startedAt - CLOCK_SKEW_MS;
}

/**
 * Which listed volume each pending entry is creating. The process names its volume only at the end, but the volume
 * is listed from the moment it exists ("Copying content"), and attempts with the same pool, name and AZ cannot be
 * told apart by their volumes. So the volumes are handed out once, one to one: newest entry first, each taking the
 * latest volume it could have created that no newer entry took. An older attempt only gets what newer ones leave,
 * and every volume is hidden behind at most one row. Failed entries take nothing (their volume is deleted,
 * VTK-3471); volumes in `taken` belong to finished processes.
 */
function allocateVolumes(entries: PendingWorkspace[], workspaces: EditingWorkspace[],
                         taken: Set<string>): Map<string, EditingWorkspace> {
  const allocated = new Map<string, EditingWorkspace>();
  const used = new Set<string>();
  for (const entry of [...entries].sort((x, y) => y.startedAt - x.startedAt)) {
    const volume = workspaces
      .filter(w => couldBeCreating(entry, w) && !isTaken(w, taken) && !used.has(w.id))
      .sort((x, y) => y.createdAt - x.createdAt)[0];
    if (volume) {
      allocated.set(entry.processId, volume);
      used.add(volume.id);
    }
  }
  return allocated;
}

function isTaken(w: EditingWorkspace, taken: Set<string>): boolean {
  return taken.has(w.id.slice(w.id.lastIndexOf('/') + 1));
}

/**
 * The pending rows still to show: a finished process is dropped once its volume is listed (the volume
 * row takes over), and a failed one stays until dismissed. While a pending row is shown, the volume it is
 * creating is not listed separately (`hide`): one workspace, one row.
 *
 * A volume a finished process names is never another entry's. Dropping that process must not hand its volume to
 * an older attempt still running, which would hide a finished workspace and its Mount until the older one ends:
 * `claim` lists the volumes of the processes dropped now, and the caller passes them back as `claimed`.
 */
export function pendingRows(entries: PendingWorkspace[], processes: Record<string, RepositoryProcessPayload>,
                            workspaces: EditingWorkspace[], claimed: string[] = []):
  { show: PendingRow[], drop: string[], claim: string[], hide: string[] } {
  const show: PendingRow[] = [];
  const drop: string[] = [];
  const claim: string[] = [];
  const hide: string[] = [];
  const taken = new Set(claimed);
  entries.forEach(e => {
    const volumeId = processes[e.processId]?.state === 'SUCCESS' ? volumeIdOf(processes[e.processId]) : undefined;
    if (volumeId) {
      taken.add(volumeId);
    }
  });
  const shown: PendingWorkspace[] = [];
  for (const entry of entries) {
    const process = processes[entry.processId];
    if (process?.state === 'SUCCESS') {
      const volumeId = volumeIdOf(process);
      if (!volumeId || workspaces.some(w => w.id.endsWith('/' + volumeId))) {
        drop.push(entry.processId);
        if (volumeId) {
          claim.push(volumeId);
        }
        continue;
      }
    }
    shown.push(entry);
  }
  // A failed creation hides nothing: its volume is deleted (VTK-3471), and if one remains it has its own row.
  const volumes = allocateVolumes(shown.filter(e => processes[e.processId]?.state !== 'FAILED'), workspaces, taken);
  for (const entry of shown) {
    const process = processes[entry.processId];
    const volume = volumes.get(entry.processId);
    if (volume) {
      hide.push(volume.id);
    }
    show.push({
      entry,
      process,
      sourceFolders: sourceFolders(entry.contents).map(folder => ({
        link: `/o/${entry.organizationId}/files/browse/${folder}`,
        name: folder.split('/').filter(Boolean).pop() || folder,
      })),
    });
  }
  return {show, drop, claim, hide};
}

/**
 * The folders the workspace's content comes from, as browse paths ("bucket/path/"), one per distinct
 * folder: a folder is its own, a file its parent's. Where the user sees each file's restore state.
 */
export function sourceFolders(contents: string[]): string[] {
  const folders = contents.map(id => {
    const path = id.replace(/^s3:\/\//, '');
    return path.endsWith('/') ? path : path.slice(0, path.lastIndexOf('/') + 1);
  });
  return [...new Set(folders)];
}

function volumeIdOf(process: RepositoryProcessPayload): string | undefined {
  try {
    return JSON.parse(process.data || '{}')?.volumeId;
  } catch {
    return undefined;
  }
}

const ACTIVE_WORKSPACE_STATUSES = new Set([
  'RESOLVING', 'RESOLVED', 'STAGING', 'STAGED',
  'WAITING_FOR_EDITOR', 'MOUNTED', 'UNMOUNTED', 'COPYING_BACK', 'FAILED',
]);


/**
 * The Mount button's pending state, per volume. Not per name: workspace names are not unique (a
 * re-created workspace, two editors picking the same label), and keying by name made every same-named
 * row spin while one of them mounted. Unmount and Delete are keyed by the id already.
 */
export function mountButtonId(ws: EditingWorkspace): string {
  return 'mount-' + ws.id;
}

@Component({
  selector: 'app-crd-management',
  templateUrl: './crd-management.component.html',
  styleUrls: ['./crd-management.component.scss'],
  imports: [
    HeadlineSectionComponent,
    LogoAreaComponent,
    CommonModule,
    DateComponent,
    RouterLink,
    ProcessButtonDirective,
    IsAllowedAnywherePipe,
  ]
})
export class CrdManagementComponent {
  organization: Signal<Organization>;
  breadCrumbs: Signal<BreadCrumb[]>;

  loading$ = signal(false);
  combined$!: Signal<{ hosts: DesktopHost[]; workspaces: EditingWorkspace[] } | undefined>;
  regionView$!: Signal<RegionView[]>;
  // The polled hosts/workspaces query, refetched right after a mount or unmount (VTK-3473).
  private listQuery?: QueryRef<Query>;
  /** What shows instead of the tables (no access, an error, loading); null once there is data. */
  readonly listState$ = signal<CrdListState>({kind: 'loading'});


  constructor(
    private apollo: Apollo,
    private contextService: ContextService,
    private banner: BannerService,
    private dialog: Dialog,
    public pb: ProcessButtonService,
    private pendingWorkspaces: PendingWorkspacesService,
  ) {
    this.organization = this.contextService.organization;

    this.breadCrumbs = computed(() => [{
      display: 'Content Platform',
      commands: ['/o', this.organization()?.id],
    }]);

    this.combined$ = toSignal(
      toObservable(this.contextService.organization).pipe(
        switchMap((org) => {
          if (!org) return of(undefined);
          const hostsPool = this.extractPool('contentplatform:repository:Query/DesktopHosts');
          const workspacesPool = this.extractPool('contentplatform:repository:Query/EditingWorkspaces');
          const pools = {hosts: hostsPool, workspaces: workspacesPool};
          this.listState$.set(crdListState(pools, undefined));
          if (this.listState$()?.kind === 'no-access') {
            this.loading$.set(false);
            return of(undefined);
          }
          this.listQuery = this.apollo.watchQuery<Query>({
            query: GET_HOSTS_AND_WORKSPACES,
            variables: { hostsInput: { pool: hostsPool }, workspacesInput: { pool: workspacesPool } },
            pollInterval: 15000,
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
          });
          return this.listQuery.valueChanges.pipe(
            tap(result => {
              this.loading$.set(result.loading);
              this.listState$.set(crdListState(pools, result));
            }),
            filter(result => !!result.data),
            map(result => {
              const hosts = (result.data?.desktopHosts || []) as DesktopHost[];
              // Clear stop spinners when instance reaches stopped/stopping state
              for (const h of hosts) {
                if ((h.state === 'stopped' || h.state === 'stopping') && this.pb.isActive('stop-' + h.instanceId)) {
                  this.pb.clear('stop-' + h.instanceId);
                }
              }
              return {
                hosts,
                workspaces: (result.data?.editingWorkspaces || []) as EditingWorkspace[],
              };
            }),
            catchError((error) => {
              this.loading$.set(false);
              this.banner.open(`Error loading desktop hosts/workspaces: ${error?.message || 'Network error'}`, ['Close']);
              return of(undefined);
            })
          );
        })
      ),
    );

    const pending = computed(() => pendingRows(
      this.pendingWorkspaces.entries(), this.pendingWorkspaces.processes(), this.combined$()?.workspaces ?? [],
      this.pendingWorkspaces.claimedVolumes()));
    // Only once the workspaces are loaded: until then every finished process would look volume-less.
    effect(() => {
      if (this.combined$()) {
        const {drop, claim} = pending();
        this.pendingWorkspaces.claim(claim);
        drop.forEach(id => this.pendingWorkspaces.remove(id));
      }
    });

    this.regionView$ = computed(() => {
      const hosts = this.combined$()?.hosts || [];
      const workspaces = (this.combined$()?.workspaces ?? []) as EditingWorkspace[];
      const pendingShown = pending().show;

      const regionSet = new Set<string>();
      pendingShown.forEach(p => regionSet.add(p.entry.availabilityZone.slice(0, -1)));
      hosts.forEach(h => regionSet.add(h.awsRegion));
      workspaces.filter(w => ACTIVE_WORKSPACE_STATUSES.has(w.status)).forEach(w => { if (w.awsRegion) regionSet.add(w.awsRegion); });

      return [...regionSet].sort().map(region => {
        const regionHosts = hosts
          .filter(h => h.awsRegion === region)
          .sort((a, b) => (a.pool || '').localeCompare(b.pool || '') || (a.name || a.instanceId).localeCompare(b.name || b.instanceId));
        const hidden = new Set(pending().hide);
        const regionWorkspaces = workspaces.filter(w =>
          w.awsRegion === region && ACTIVE_WORKSPACE_STATUSES.has(w.status) && !hidden.has(w.id));
        return {
          region,
          city: this.regionCity(region),
          hosts: regionHosts.map(host => ({ host, workspaces: regionWorkspaces.filter(w => w.mountedOn === host.instanceId) })),
          pending: pendingShown.filter(p => p.entry.availabilityZone.slice(0, -1) === region),
          unassigned: regionWorkspaces.filter(w => !w.mountedOn),
        };
      });
    });
  }

  dismissPending(row: PendingRow) {
    this.pendingWorkspaces.remove(row.entry.processId);
  }

  formatTimestamp(timestamp: number): Date {
    return new Date(timestamp * 1000);
  }

  startSession(instance: DesktopHost) {
    const buttonId = 'start-' + instance.instanceId;
    this.pb.setPending(buttonId);
    this.apollo.mutate({
      mutation: START_DESKTOP_HOST,
      variables: {
        input: {
          pool: instance.pool,
          hostInstance: instance.instanceId,
          region: instance.awsRegion,
        }
      }
    }).subscribe({
      next: (result) => {
        const process = (result.data as any)?.startDesktopHost as RepositoryProcessPayload;
        if (process?.id) {
          const name = instance.name || instance.instanceId;
          this.pb.bind(buttonId, process, (state, p) => {
            if (state === 'FAILED') this.banner.open(`Setup of ${name} failed: ${p.message || 'Unknown error'}`, ['Close']);
          });
        } else {
          this.pb.clear(buttonId);
        }
      },
      error: (error) => {
        this.pb.clear(buttonId);
        const name = instance.name || instance.instanceId;
        this.banner.open(`Failed to start ${name}: ${error?.message || 'Unknown error'}`, ['Close']);
      }
    });
  }


  /** Setup opens the host to the internet, so it first unmounts every workspace (kept, to mount again later). */
  setupSession(instance: DesktopHost, mountedWorkspaces: number) {
    const name = instance.name || instance.instanceId;
    const unmount = mountedWorkspaces === 1
      ? 'unmounts the workspace mounted on it (it is kept and can be mounted again), '
      : mountedWorkspaces > 1
        ? `unmounts the ${mountedWorkspaces} workspaces mounted on it (they are kept and can be mounted again), `
        : '';
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        headline: 'Set Up Desktop Host',
        message: `Setup of <strong>${name}</strong> ${unmount}reinstalls the remote desktop software and opens internet `
          + 'access. It keeps internet access until a workspace is mounted, which cuts it off and restarts the host.',
        actionName: 'Set up',
        action: () => this._setup(instance),
      }
    });
  }

  private _setup(instance: DesktopHost) {
    const buttonId = 'setup-' + instance.instanceId;
    this.pb.setPending(buttonId);
    this.apollo.mutate({
      mutation: SETUP_DESKTOP_HOST,
      variables: {
        input: {
          pool: instance.pool,
          hostInstance: instance.instanceId,
          region: instance.awsRegion,
        }
      }
    }).subscribe({
      next: (result) => {
        const process = (result.data as any)?.setupDesktopHost as RepositoryProcessPayload;
        if (process?.id) {
          const name = instance.name || instance.instanceId;
          this.pb.bind(buttonId, process, (state, p) => {
            if (state === 'FAILED') this.banner.open(`Setup of ${name} failed: ${p.message || 'Unknown error'}`, ['Close']);
          });
        } else {
          this.pb.clear(buttonId);
        }
      },
      error: (error) => {
        this.pb.clear(buttonId);
        const name = instance.name || instance.instanceId;
        this.banner.open(`Failed to set up ${name}: ${error?.message || 'Unknown error'}`, ['Close']);
      }
    });
  }

  stopSession(instance: DesktopHost) {
    const name = instance.name || instance.instanceId;
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        headline: 'Stop Desktop Host',
        message: `Do you really want to stop <strong>${name}</strong>?`,
        actionName: 'Stop',
        action: () => {
          this.pb.setPending('stop-' + instance.instanceId);
          this.apollo.mutate({
            mutation: STOP_DESKTOP_HOST,
            variables: {
              input: {
                pool: instance.pool,
                hostInstance: instance.instanceId,
                region: instance.awsRegion,
              }
            }
          }).subscribe({
            next: () => {},
            error: (error) => {
              this.pb.clear('stop-' + instance.instanceId);
              this.banner.open(`Failed to stop ${name}: ${error?.message || 'Unknown error'}`, ['Close']);
            },
          });
        }
      }
    });
  }

  /** Extract pool value from policy resources for a given action.
   *  e.g. resource "contentplatform:pool:input.pool/*" → "*",
   *       resource "contentplatform:pool:input.pool/above_above" → "above_above" */
  private extractPool(action: string): string | null {
    return firstGrantedPattern(grantedPatterns(this.contextService.policies(), action, POOL_RESOURCE));
  }

  getEditStatusClass(status: string): string {
    switch (status) {
      case 'STAGED': case 'WAITING_FOR_EDITOR': case 'MOUNTED': return 'cl-color-leaf';
      case 'RESOLVING': case 'RESOLVED': case 'STAGING': case 'COPYING_BACK': return 'cl-color-honey';
      case 'FAILED': return 'cl-color-brick';
      default: return 'cl-color-ash';
    }
  }

  getEditStatusLabel(status: string): string {
    switch (status) {
      case 'RESOLVING': case 'RESOLVED': return 'Provisioning';
      case 'STAGING': return 'Copying content';
      case 'STAGED': case 'WAITING_FOR_EDITOR': return 'Ready';
      case 'MOUNTED': return 'Mounted';
      case 'UNMOUNTED': return 'Detached';
      case 'COPYING_BACK': return 'Saving changes';
      case 'FAILED': return 'Failed';
      default: return status;
    }
  }

  isMounting(ws: EditingWorkspace): boolean { return this.pb.isActive(mountButtonId(ws)); }

  volumeId(ws: EditingWorkspace): string {
    return ws.id.split('/').pop() || ws.id;
  }

  /** Where a mounted workspace is on its host: "E:" on Windows, the directory (mountPath) on Linux. Two fields, not a
   *  fallback: a workspace gets one or the other, from its host's OS (mount_editing_workspace). */
  mountedAs(ws: EditingWorkspace): string {
    return ws.mountPath || (ws.driveLetter ? `${ws.driveLetter}:` : '');
  }

  regionCity(region: string): string {
    const cities: Record<string, string> = {
      'eu-west-1': 'Dublin', 'eu-central-1': 'Frankfurt', 'eu-south-2': 'Madrid',
      'us-east-1': 'Virginia', 'us-west-2': 'Oregon', 'af-south-1': 'Cape Town',
    };
    return cities[region] || region;
  }

  isMountable(ws: EditingWorkspace): boolean {
    return ['STAGED', 'WAITING_FOR_EDITOR', 'UNMOUNTED'].includes(ws.status);
  }

  hostsInSameAz(ws: EditingWorkspace): DesktopHost[] {
    return (this.combined$()?.hosts || []).filter(
      h => (h.state === 'registered' || h.state === 'in_use') && h.availabilityZone === ws.availabilityZone
    );
  }

  showMountDialog(ws: EditingWorkspace) {
    const hosts = this.hostsInSameAz(ws);
    this.dialog.open(MountDialogComponent, {
      data: {
        workspace: ws,
        hosts,
        // The pool to send per host, from the caller's own policies; null = not allowed onto that host.
        poolFor: (host: DesktopHost) => mountPool(this.contextService.policies(), ws.pool, host.pool),
        onMount: (host: DesktopHost, pool: string, done: (success: boolean) => void) => {
          this.mountWorkspaceWithCallback(ws, host, pool, done);
        },
      },
    });
  }

  /**
   * A mount/unmount process has succeeded, but the row still shows the status of the last poll, up to
   * 15 s old: after "Mount here" it read READY with an active Mount button (VTK-3473). Keep the button
   * busy and fetch the list now; the spinner ends when the row shows the new status.
   */
  private refreshAfter(buttonId: string) {
    this.pb.setPending(buttonId);
    const settled = () => this.pb.clear(buttonId);
    (this.listQuery?.refetch() ?? Promise.resolve()).then(settled, settled);
  }

  private mountWorkspaceWithCallback(ws: EditingWorkspace, host: DesktopHost, pool: string,
                                     done: (success: boolean) => void) {
    const buttonId = mountButtonId(ws);
    this.pb.setPending(buttonId);
    this.apollo.mutate({
      mutation: MOUNT_EDITING_WORKSPACE,
      variables: {
        input: {
          pool,
          id: ws.id,
          hostInstance: host.instanceId,
        }
      }
    }).subscribe({
      next: (result) => {
        const process = (result.data as any)?.mountEditingWorkspace as RepositoryProcessPayload;
        if (process?.id) {
          this.pb.bind(buttonId, process, (state, p) => {
            if (state === 'FAILED') this.banner.open(`Failed to mount ${ws.mountPoint}: ${p.message || 'Unknown error'}`, ['Close']);
            if (state === 'SUCCESS') this.refreshAfter(buttonId);
            done(state === 'SUCCESS');
          });
        } else {
          this.pb.clear(buttonId);
          done(false);
        }
      },
      error: (error) => {
        this.pb.clear(buttonId);
        this.banner.open(`Failed to mount ${ws.mountPoint}: ${error?.message || 'Unknown error'}`, ['Close']);
        done(false);
      },
    });
  }

  unmountWorkspace(ws: EditingWorkspace, action: 'UNMOUNT' | 'COPY_BACK' | 'DELETE') {
    const name = ws.mountPoint || ws.id;
    const mountInfo = ws.mountedOn ? ` It is currently mounted as <strong>${this.mountedAs(ws)}</strong> on <strong>${ws.mountedOn}</strong>.` : '';
    const messages: Record<string, string> = {
      'UNMOUNT': `Unmount <strong>${name}</strong> and keep the volume. The content remains on disk and can be remounted later.${mountInfo}`,
      'COPY_BACK': `Copy the content of <strong>${name}</strong> back to S3, then delete the volume.${mountInfo}`,
      'DELETE': `Permanently delete the volume <strong>${name}</strong> and all its content. This cannot be undone.${mountInfo}`,
    };
    const actionNames: Record<string, string> = {
      'UNMOUNT': 'Unmount',
      'COPY_BACK': 'Copy back and delete',
      'DELETE': 'Delete',
    };
    const headlines: Record<string, string> = {
      'UNMOUNT': `Unmount ${name}`,
      'COPY_BACK': `Copy back and delete ${name}`,
      'DELETE': `Delete ${name}`,
    };
    this.dialog.open(ConfirmDialogComponent, {
      data: {
        headline: headlines[action],
        message: messages[action],
        actionName: actionNames[action],
        danger: action === 'DELETE' || action === 'COPY_BACK',
        action: () => {
          const buttonId = 'unmount-' + ws.id + '-' + action;
          this.pb.setPending(buttonId);
          this.apollo.mutate({
            mutation: UNMOUNT_EDITING_WORKSPACE,
            variables: {
              input: {
                pool: ws.pool,
                id: ws.id,
                action,
              }
            }
          }).subscribe({
            next: (result) => {
              const process = (result.data as any)?.unmountEditingWorkspace as RepositoryProcessPayload;
              if (process?.id) {
                this.pb.bind(buttonId, process, (state, p) => {
                  if (state === 'FAILED') this.banner.open(`Failed to unmount ${ws.mountPoint}: ${p.message || 'Unknown error'}`, ['Close']);
                  if (state === 'SUCCESS') this.refreshAfter(buttonId);
                });
              } else {
                this.pb.clear(buttonId);
              }
            },
            error: (error) => {
              this.pb.clear(buttonId);
              this.banner.open(`Failed to unmount ${ws.mountPoint}: ${error?.message || 'Unknown error'}`, ['Close']);
            },
          });
        }
      }
    });
  }
}
